import {
	Dialog,
	Form,
	FormItem,
	renderReactDialog,
	Toolbar
} from "client/Components";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useDataInputDialog,
	useUnmountedCancellation,
	useWorkspaceMediaQuery
} from "client/Hooks";
import {
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind,
	IRootValidation,
	IValidationConfiguration
} from "client/Validation";
import {
	inline,
	isNotEmpty,
	isNotNullNorUndefined
} from "client/Validation/rules";
import { NewDocumentType, OfficeDocumentEditor } from "client/Api/Documents/common";
import {
	getItemsInPreferredOrder,
	UserEditorPreference
} from "client_legacy/Documents/officeDocumentsOpenPreference";
import {
	getOpenLocalizationsForNewDocumentType,
	OpenLocalizations
} from "client_legacy/Documents/openLocalizations";
import { isValid } from "client_legacy/Documents/WebDav/documentNamePolicy";
import { publish, WebDavEvent } from "client_legacy/Documents/WebDav/events";
import {
	createDocument,
	CreateDocumentModel
} from "client/Api/Documents/WebDav/webDavActions";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { never } from "client_legacy/ndaw/errors";
import {
	WebDavConfiguration
} from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import {
	ReactElement,
	useCallback,
	useMemo,
	useState
} from "react";
import { TextInputWithExtension } from "../../Dialogs/TextInputWithExtension";
import { useEditorPreference } from "../../useEditorPreference";
import * as wopiOpener from "client_legacy/Documents/WebDav/wopiOpener";
import * as localAppOpener from "client_legacy/Documents/WebDav/localAppOpener";
import { showOpeningInLocalEditorWithOptionToOpenInOnlineEditor } from "client_legacy/Documents/misc";
import { FileSystemItemModel, WebDavServerModel } from "client/Api/Documents/WebDav/webDav";

type NewDocumentDialogProps = {
	server: WebDavServerModel;
	folderId: string;
	documentType: Exclude<NewDocumentType, NewDocumentType.OneNote>;
	webDavConfiguration: WebDavConfiguration;
	onClose: () => void;
};

const NewDocumentDialog = (props: NewDocumentDialogProps): ReactElement => {
	const {
		server,
		folderId,
		documentType,
		webDavConfiguration,
		onClose
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [formData, setFormData] = useState<FormData>({
		name: ""
	});

	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const alertManager: AlertManager = useAlerts();
	const editorPreference: UserEditorPreference = useEditorPreference();

	const formHandler: IFormHandler<FormData> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	const { title, extension } = useMemo(
		(): { title: string; extension: string; } => {
			switch (documentType) {
				case NewDocumentType.Word:
					return {
						title: localize("_CreateANewWordDocument"),
						extension: ".docx"
					};
				case NewDocumentType.Excel:
					return {
						title: localize("_CreateANewExcelWorkbook"),
						extension: ".xlsx"
					};
				case NewDocumentType.PowerPoint:
					return {
						title: localize("_CreateANewPowerPointPresentation"),
						extension: ".pptx"
					};
				default:
					never(documentType);
			}
		},
		[documentType]
	);

	const openLocalizations = useMemo(
		(): OpenLocalizations => getOpenLocalizationsForNewDocumentType(documentType),
		[documentType]
	);

	const {
		wopi,
		localApp,
		defaultOfficeDocumentEditor
	} = webDavConfiguration;

	const createHandler = useCallback(
		async (): Promise<TryResult<FileSystemItemModel>> => {
			if (isBusy) {
				return {
					wasSuccessful: false
				};
			}

			setIsBusy(true);
			const validation: IRootValidation<FormData> = formHandler.validation;

			try {
				const validationResult: InputValidationResult = await validation
					.validate(globalCancellation);

				if (validationResult.kind !== InputValidationResultKind.Valid) {
					setIsBusy(false);
					return {
						wasSuccessful: false
					};
				}

				const model: CreateDocumentModel = {
					...formData,
					name: (formData.name + extension).trim(),
					folderId,
					documentType
				};

				const createdDocument: FileSystemItemModel = await createDocument(
					server.id,
					model,
					globalCancellation
				);

				publish(
					WebDavEvent.CreatedOrReplaced,
					{
						serverId: server.id,
						item: createdDocument
					}
				);

				return {
					wasSuccessful: true,
					result: createdDocument
				};
			} catch (e) {
				if (isCancelledError(e)) {
					return {
						wasSuccessful: false
					};
				}

				if (!validation.tryProcessServerError(e)) {
					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
				}

				setIsBusy(false);
			}

			return {
				wasSuccessful: false
			};
		},
		[
			alertManager,
			documentType,
			extension,
			folderId,
			formData,
			formHandler.validation,
			globalCancellation,
			isBusy,
			server.id
		]
	);

	const createAndOpenOnlineHandler = useCallback(
		async (): Promise<void> => {
			const createResult: TryResult<FileSystemItemModel> = await createHandler();

			if (!createResult.wasSuccessful) {
				return;
			}

			onClose();

			const createdDocument: FileSystemItemModel = createResult.result;

			wopiOpener.open(
				server.id,
				createdDocument.id,
				createdDocument.name
			);
		},
		[
			createHandler,
			onClose,
			server
		]
	);

	const createAndOpenInLocalAppHandler = useCallback(
		async (): Promise<void> => {
			const createResult: TryResult<FileSystemItemModel> = await createHandler();

			if (!createResult.wasSuccessful) {
				return;
			}

			onClose();

			const createdDocument: FileSystemItemModel = createResult.result;

			localAppOpener.open(server, createdDocument);
			if (wopi.isEnabled) {
				showOpeningInLocalEditorWithOptionToOpenInOnlineEditor(
					openLocalizations,
					(): void => {
						wopiOpener.open(
							server.id,
							createdDocument.id,
							createdDocument.name
						);
					}
				);
			}
		},
		[
			createHandler,
			onClose,
			openLocalizations,
			server,
			wopi.isEnabled
		]
	);

	const submitHandler = useCallback(
		(): void => {
			const action: () => void = getItemsInPreferredOrder(
				defaultOfficeDocumentEditor,
				[
					[
						OfficeDocumentEditor.Online,
						() => {
							if (!wopi.isEnabled) {
								return null;
							}

							return (): void => {
								createAndOpenOnlineHandler();
							};
						}
					],
					[
						OfficeDocumentEditor.Local,
						() => {
							if (!localApp.isEnabled) {
								return null;
							}

							return (): void => {
								createAndOpenInLocalAppHandler();
							};
						}
					]
				],
				editorPreference
			)[0];

			action();
		},
		[
			createAndOpenInLocalAppHandler,
			createAndOpenOnlineHandler,
			defaultOfficeDocumentEditor,
			editorPreference,
			localApp.isEnabled,
			wopi.isEnabled
		]
	);

	return (
		<Dialog
			trackingId="webdav-new-document-dialog"
			hasBusyIndicator={isBusy}
		>
			<Dialog.Header
				title={title}
				onClose={onClose}
			/>

			<Dialog.Body>
				<Form onSubmit={submitHandler}>
					<FormItem label={localize("_DocumentName")} isRequired>
						<TextInputWithExtension
							attachable={formHandler.for("name")}
							fileExtension={extension}
							placeholder={localize("_EnterDocumentName")}
							isMobile={isMobile}
						/>
					</FormItem>
				</Form>
			</Dialog.Body>

			<Dialog.Footer>
				<Toolbar>
					{getItemsInPreferredOrder<ReactElement | null>(
						defaultOfficeDocumentEditor,
						[
							[
								OfficeDocumentEditor.Online,
								(): ReactElement | null => {
									if (wopi.isEnabled) {
										return (
											<Toolbar.Button
												key="open-online"
												kind="primary"
												onClick={createAndOpenOnlineHandler}
												text={openLocalizations.online.action}
												trackingId="open-online"
											/>
										);
									}

									return null;
								}
							],
							[
								OfficeDocumentEditor.Local,
								(): ReactElement | null => {
									if (localApp.isEnabled) {
										return (
											<Toolbar.Button
												key="open-in-local-app"
												kind="primary"
												onClick={createAndOpenInLocalAppHandler}
												text={openLocalizations.local.action}
												trackingId="open-in-local-app"
											/>
										);
									}

									return null;
								}
							]
						],
						editorPreference
					)}

					<Toolbar.Button
						kind="secondary"
						trackingId="cancel"
						text={localize("_Cancel")}
						onClick={onClose}
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
NewDocumentDialog.displayName = "NewDocumentDialog";

type FormData = Omit<CreateDocumentModel, "folderId" | "documentType">;

const validationConfiguration: IValidationConfiguration<FormData> =
	createValidationConfiguration<FormData>();

validationConfiguration.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		inline(
			(value): InputValidationResult => {
				if (value !== null) {
					if (!isValid(value)) {
						return {
							kind: InputValidationResultKind.Invalid,
							errorMessage: localize("_ContainsIllegalCharactersForWebDav")
						};
					}
				}

				return {
					kind: InputValidationResultKind.Valid
				};
			}
		)
	);

export type NewDocumentDialogInitialData = Omit<
	NewDocumentDialogProps,
	"onClose"
>;

export function useNewDocumentDialog(): [
	element: ReactElement | null,
	open: (props: NewDocumentDialogInitialData) => Promise<void>
] {
	const [element, originalOpen] = useDataInputDialog<NewDocumentDialogInitialData, void>(
		(props, onClose) => (
			<NewDocumentDialog
				{...props}
				onClose={onClose}
			/>
		)
	);

	const open = useCallback(
		async (props: NewDocumentDialogInitialData): Promise<void> => {
			await originalOpen(props);
		},
		[originalOpen]
	);

	return [element, open];
}

export function openNewDocumentDialog(props: Omit<NewDocumentDialogProps, "onClose">): void {
	renderReactDialog(
		(onClose) => (
			<NewDocumentDialog
				{...props}
				onClose={onClose}
			/>
		)
	);
}
